import React, {createRef, useContext, useEffect, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import MaterialTable from "@material-table/core";
import {TablePagination, useTheme} from "@material-ui/core";
import SystemContext from '../../../SystemContext';
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import {
  useTripClients,
  useAddTripClient,
  useUpdateTripClient,
  useDeleteTripClient,
} from "../../../services/trips/";
import ClientCompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import AirportLookup from "../../Airports/components/AirportsLookup";
import {ValidatorColumn, ColumnWidth} from "../../../blocks/ColumnHelper";
import TripClientsValidator from "../validators/TripClientsValidator";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { ComboSelect, MaskedNumber, MaskedText, UldAllocationSelect } from '../../../controls';
import ContractSelector from '../../../controls/ContractSelector';
import ContractSelectDialog from "../../Contracts/components/ContractSelectDialog";

const apiSitePrefix = getApiSitePrefix();


const TripClients = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const props = {
    canEdit: true,
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit
  });

  const SystemData = useContext(SystemContext);
  const tableRef = createRef({state: {lastEditingRow: false}});
  const eventBus = SystemData.eventBus;
  useEffect(() => {
    const handleAddRow = () => {
      if (tableRef  && tableRef.current) {
        tableRef.current.dataManager.changeRowEditing();
        tableRef.current.setState({
          ...tableRef.current.dataManager.getRenderState(),
          showAddRow: !tableRef.current.state.showAddRow,
        });
      }
    }
    if (eventBus) {
      eventBus.bind('TripClients.Add', handleAddRow);
      return ()=>{
        eventBus.unbind('TripClients.Add', handleAddRow);
      }
    }
  }, [eventBus, tableRef])

  const tripUuid = props.tripUuid;
  const tripRoute = props.tripRoute;
  const onwardDestinations = props.onwardDestinations;

  const handleAddTripClient = useAddTripClient();
  const handleUpdateTripClient = useUpdateTripClient();
  const handleDeleteTripClient = useDeleteTripClient();

  const extraKeys = [tripUuid];
  const queryClient = useQueryClient();
  const tripClients = useTripClients({tableRef, queryClient, extraKeys});

  const handleAddContract = async (contractUuid) => {
    const headers = {...axios.defaults.headers, 'Content-Type': 'application/json'};

    try {
      SystemData.toggleLoadingProgress(true, "Adding contract allotmwnts for trip ...");
      const response = await axios.post(
        `${apiSitePrefix}/tripclients/addContract`,
        {contractUuid, tripUuid},
        { headers }
      );
      SystemData.toggleLoadingProgress(false);

      //console.error(response);
      if (response && response.data) {
        if (response.data.success) {
          queryClient.invalidateQueries(['TripClients', ...extraKeys]);
        } else {
          SystemData.openAlert("Add Contract Allotments", "Could not add contract allotments due to this error: " + response.data.error);
        }
      } else {
        SystemData.openAlert("Add Contract Allotments", "Could not add contract allotments due to an unknown error");
      }
    } catch (err) {
      SystemData.toggleLoadingProgress(false);
      console.error(err);
      SystemData.openAlert("Add Contract Allotments", "Could not add contract allotments due to this error: " + err.message);
    }
  }

  const getTripClientColumns = (props) => {
    const baseColumn = {
      cellStyle: {padding: "8px 2px"},
      headerStyle: {padding: "8px 2px"},
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };

    const formatChars = {
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const additionalValues = {route: tripRoute, onwardDestinations: onwardDestinations};
    const validatorColumn = ValidatorColumn(TripClientsValidator, false, additionalValues);

    return [
      {...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(200),
        cellStyle: {padding: "8px 2px 8px 12px"},
        headerStyle: {padding: "8px 2px 8px 12px"},
        title: "Client Company",
        field: "companyName",
        initialEditValue: "",
        editable: props.canEdit ? "onAdd" : "never",
        editComponent: (props) => (
          <ClientCompanyUuidLookup
            companyType={"CUSTOMER"}
            includeFreesale={true}
            value={props.value}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const customer = returnObject.data
                const data = {
                  ...props.rowData,
                  companyName: customer.companyName,
                  companyUuid: customer.companyUuid,
                  companyCode: customer.companyCode,
                  branchCode: customer.branchCode
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined, undefined, {textAlign: "center"}, {textAlign: "center"}, {textAlign: "center"}),
        title: "Origin",
        field: "origin",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70,undefined, undefined, {textAlign: "center"}, {textAlign: "center"}, {textAlign: "center"}),
        title: "Dest.",
        field: "destination",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Allotment Code",
        field: "allotmentCode",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Contract Ref.",
        field: "contractUuid",
        initialEditValue: "",
        sorting: false,
        editComponent: (props) => (
          <div style={{marginTop: -3}}>
            <ContractSelector
              id="contractUuid"
              companyUuid={props.rowData.companyUuid}
              fullWidth
              value={props.value || ""}
              onChange={props.onChange}
            />
          </div>
        ),
        render: (rowData) => (
          <ContractSelector
            id="contractUuid"
            companyUuid={rowData.companyUuid}
            fullWidth
            value={rowData.contractUuid || ""}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        title: "ULD Allocation",
        field: "uldAllocation",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <UldAllocationSelect
            value={props.value}
            onChange={(record) => {
              props.onRowDataChange({...props.rowData, ...record});
            }}
          />
        ),
        render: (rowdata) => (
          <UldAllocationSelect
            value={rowdata.uldAllocation}
            readOnly={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(160,undefined, undefined, {paddingRight: 10}, {paddingRight: 10}, {paddingRight: 10}, ),
        title: "Allotment KG",
        field: "allotmentKg",
        type: "numeric",
        sorting: false,
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(6)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (rowdata) => (
          <MaskedNumber
            displayType="text"
            value={rowdata.allotmentKg}
            fixedDecimalScale={true}
            readOnly={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(200),
        cellStyle: {padding: "8px 2px 8px 12px"},
        headerStyle: {padding: "8px 2px 8px 12px"},
        title: "Origin Cargo Handler",
        field: "originCargoHandlerCompanyName",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <ClientCompanyUuidLookup
            companyType={"CGHA"}
            atAirport={props.rowData.origin}
            value={props.value}
            returnType={"object"}
            allowEmpty={true}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const customer = returnObject.data;
                const data = {
                  ...props.rowData,
                  originCargoHandlerCompanyName: customer.companyName,
                  originCargoHandlerUuid: customer.companyUuid,
                  originCargoHandlerCompanyCode: customer.companyCode,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(200),
        cellStyle: {padding: "8px 2px 8px 12px"},
        headerStyle: {padding: "8px 2px 8px 12px"},
        title: "Destination Cargo Handler",
        field: "destinationCargoHandlerCompanyName",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <ClientCompanyUuidLookup
            companyType={"CGHA"}
            atAirport={props.rowData.destination}
            value={props.value}
            returnType={"object"}
            allowEmpty={true}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const customer = returnObject.data
                const data = {
                  ...props.rowData,
                  destinationCargoHandlerCompanyName: customer.companyName,
                  destinationCargoHandlerUuid: customer.companyUuid,
                  destinationCargoHandlerCompanyCode: customer.companyCode,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,6,undefined,undefined,undefined,undefined,false),
        title: "Delivered",
        field: "cargoDeliveredAs",
        editable: state.canEdit ? "always" : "never",
        sorting: false,
        render: (rowData) => (
          <ComboSelect
            id="cargoDeliveredAs"
            group="CARGO DELIVERED AS"
            subGroup=""
            fullWidth
            value={rowData.cargoDeliveredAs}
            displayOnly={true}
            onChange={props.onChange}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="cargoDeliveredAs"
            group="CARGO DELIVERED AS"
            subGroup=""
            fullWidth
            value={props.value || ""}
            onChange={props.onChange}
          />
        )
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{marginLeft: props.mode === "DIALOG"?0:50}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          backgroundColor: "#fdfdfd",
          border: "2px solid #aaaaaa",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.childTableHeader.backgroundColor,
            color: theme.palette.childTableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 5,
          detailPanelType: "single",
          padding: 'dense',
          toolbar: props.mode !== "DIALOG",
          search: props.mode !== "DIALOG",
          showTitle: props.mode !== "DIALOG",
        }}
        actions={state.canEdit
          ?
            [
              {
                hidden: false,
                disabled: false,
                icon: 'request_quote',
                iconProps: { style: (state.canEdit) ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}},
                tooltip: "Add Contracted Allotments",
                isFreeAction: true,
                onClick: (e, rowData) => {
                  if (eventBus) {
                    eventBus.trigger("ContractSelect.open", {title: null, message: "Select contract to associate to trip", canEdit: state.canEdit});
                  }
                }
              }
            ]
          : undefined
        }
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        title="Trip Client Cargo Allotments"
        columns={getTripClientColumns({canEdit: state.canEdit})}
        data={tripClients.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: state.canEdit
            ? (newData) => new Promise((resolve, reject) => {
              handleAddTripClient.mutate({record: {...newData, tripUuid: tripUuid}, resolve, reject, extraKeys, postError: (err) => {
                SystemData.alert("Error", err?.errorMessage || "An unexpected error has occurred");
              }});
            })
            : undefined,
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject) => {
              handleUpdateTripClient.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys, postError: (err) => {
                SystemData.alert("Error", err?.errorMessage || "An unexpected error has occurred");
              }});
            }),
          onRowDelete: (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteTripClient.mutate({oldRecord: oldData, resolve, reject, extraKeys});
            }),
        }}
      />
      <ContractSelectDialog
        systemData={SystemData}
        onChange={(contractUuid) => {
          console.log(contractUuid)
          if (contractUuid) {
            handleAddContract(contractUuid)
          }
        }}
      />
    </div>
  );

}

export default TripClients;
